/** Pieces the Marketing pages share: a file with its preview, and a copyable text. */
import { useState } from 'react';
import { Button } from '@shared-ui/components';
import type { MarketingAsset } from '../../api/marketingApi';
import { sizeLabel } from './episodeContent';

/** One file: a preview you can look at, its name, and a download. */
export function AssetTile({ asset, label }: { asset: MarketingAsset; label?: string }) {
  return (
    <figure className="mk-asset">
      <div className="mk-asset-preview">
        {asset.media === 'video'
          ? <video src={asset.url} controls muted playsInline preload="metadata" />
          : <img src={asset.url} alt={asset.name} loading="lazy" />}
      </div>
      <figcaption>
        {label && <strong>{label}</strong>}
        <span className="mk-asset-name" title={asset.path}>{asset.name}</span>
        <span className="mk-sub">{asset.media === 'video' ? 'Video' : 'Image'} · {sizeLabel(asset.size)}</span>
        <a className="mk-download" href={asset.url} download={asset.name}>Download</a>
      </figcaption>
    </figure>
  );
}

/** A slot for a file the kit needs but storage does not have. */
export function MissingTile({ label, hint }: { label: string; hint: string }) {
  return (
    <figure className="mk-asset mk-asset--missing">
      <div className="mk-asset-preview"><span>Not in storage</span></div>
      <figcaption><strong>{label}</strong><span className="mk-sub">{hint}</span></figcaption>
    </figure>
  );
}

/** Text an admin pastes somewhere else: shown in full, copied with one click. */
export function CopyField({ label, text }: { label: string; text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* Clipboard refused: the text is on screen to select by hand. */
    }
  };
  return (
    <div className="mk-copy">
      <div className="mk-copy-head">
        <span className="mk-copy-label">{label}</span>
        <Button size="sm" variant="secondary" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
      </div>
      <pre className="mk-copy-text">{text}</pre>
    </div>
  );
}
